import { ChevronLeft, ChevronRight, Download, FileText, Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { useGetFileDownloadUrl } from '@/frontend/hooks/api/files/use-get-file-download-url'
import { useFileDownloads } from '@/frontend/hooks/use-file-downloads'

/** Minimal file shape the viewer needs to preview and download a file. */
export type ViewerFile = { id: string; fileName: string; contentType: string }

/**
 * Lightbox-style viewer for paging through a patient's files without opening
 * each in a new tab. Previews images and PDFs inline, supports prev/next via
 * the on-screen chevrons or the Left/Right arrow keys, and offers a direct
 * download. `index` is the position of the file to show (or `null` when closed).
 */
export function FileViewerDialog({
  files,
  index,
  onIndexChange,
  onClose,
}: {
  files: ViewerFile[]
  index: number | null
  onIndexChange: (index: number) => void
  onClose: () => void
}) {
  const open = index !== null
  const file = index !== null ? (files[index] ?? null) : null
  const { data: url, isLoading } = useGetFileDownloadUrl(file?.id ?? null)
  const { downloadFile } = useFileDownloads()

  // Focus target for the preview area. Keystrokes inside a cross-origin PDF
  // <iframe> are unreachable from here, so we can't intercept arrows once the
  // user clicks into the PDF. Refocusing this region on open and after each
  // navigation pulls focus back out of the iframe, so a single chevron click
  // re-arms Left/Right paging instead of needing a manual click on the dialog.
  const viewerRef = useRef<HTMLDivElement>(null)

  const hasPrev = index !== null && index > 0
  const hasNext = index !== null && index < files.length - 1

  // Arrow-key navigation while the viewer is open — lets users flick through
  useEffect(() => {
    if (!open) return
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'ArrowLeft' && hasPrev) {
        event.preventDefault()
        onIndexChange(index! - 1)
      } else if (event.key === 'ArrowRight' && hasNext) {
        event.preventDefault()
        onIndexChange(index! + 1)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, index, hasPrev, hasNext, onIndexChange])

  // Pull focus back to the dialog on open and whenever the shown file changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: index is an intentional trigger — re-run to refocus on navigation, even though the body doesn't read it.
  useEffect(() => {
    if (open) viewerRef.current?.focus()
  }, [open, index])

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="flex h-[85vh] w-full max-w-[95vw] flex-col gap-0 p-0 sm:max-w-5xl">
        <DialogHeader className="flex flex-row items-center justify-between gap-3 border-b px-4 py-3 pr-12">
          <div className="min-w-0">
            <DialogTitle className="truncate">{file?.fileName ?? 'File'}</DialogTitle>
            {index !== null && (
              <p className="text-sm text-muted-foreground">
                {index + 1} of {files.length}
              </p>
            )}
          </div>
          {file && (
            <Button variant="outline" onClick={() => downloadFile(file)}>
              <Download />
              Download
            </Button>
          )}
        </DialogHeader>

        <div
          ref={viewerRef}
          tabIndex={-1}
          className="relative flex flex-1 items-center justify-center overflow-hidden bg-muted/40 p-2 outline-none"
        >
          {hasPrev && (
            <Button
              variant="outline"
              size="icon"
              aria-label="Previous file"
              className="absolute left-3 z-10 rounded-full shadow-sm"
              onClick={() => onIndexChange(index! - 1)}
            >
              <ChevronLeft />
            </Button>
          )}

          <FilePreview file={file} url={url} isLoading={isLoading} onDownload={downloadFile} />

          {hasNext && (
            <Button
              variant="outline"
              size="icon"
              aria-label="Next file"
              className="absolute right-3 z-10 rounded-full shadow-sm"
              onClick={() => onIndexChange(index! + 1)}
            >
              <ChevronRight />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Renders the inline preview for the current file based on its content type. */
function FilePreview({
  file,
  url,
  isLoading,
  onDownload,
}: {
  file: ViewerFile | null
  url: string | undefined
  isLoading: boolean
  onDownload: (file: ViewerFile) => void
}) {
  if (!file) return null

  if (isLoading || !url) {
    return <Loader2 className="size-8 animate-spin text-muted-foreground" />
  }

  if (file.contentType.startsWith('image/')) {
    return <img src={url} alt={file.fileName} className="max-h-full max-w-full object-contain" />
  }

  if (file.contentType === 'application/pdf') {
    return <iframe src={url} title={file.fileName} className="h-full w-full border-0" />
  }

  if (file.contentType.startsWith('video/')) {
    // key by id so the error fallback resets when paging to another video
    return <VideoPreview key={file.id} file={file} url={url} onDownload={onDownload} />
  }

  // Word/Excel/CSV/etc. can't be previewed inline — offer a download instead.
  return <UnpreviewableFile file={file} onDownload={onDownload} />
}

/**
 * Inline video player. Some codecs/containers (e.g. many .mov files) can't be
 * decoded by the browser, so we fall back to the download prompt on error.
 */
function VideoPreview({
  file,
  url,
  onDownload,
}: {
  file: ViewerFile
  url: string
  onDownload: (file: ViewerFile) => void
}) {
  const [failed, setFailed] = useState(false)

  if (failed) {
    return <UnpreviewableFile file={file} onDownload={onDownload} />
  }

  return (
    // biome-ignore lint/a11y/useMediaCaption: user-uploaded videos have no caption track available
    <video src={url} controls className="max-h-full max-w-full" onError={() => setFailed(true)}>
      Your browser can't play this video.
    </video>
  )
}

/** Fallback shown when a file can't be previewed inline — offers a download instead. */
function UnpreviewableFile({
  file,
  onDownload,
}: {
  file: ViewerFile
  onDownload: (file: ViewerFile) => void
}) {
  return (
    <div className="flex flex-col items-center gap-3 text-center text-muted-foreground">
      <FileText className="size-10" />
      <p>This file can't be previewed. Download it to view.</p>
      <Button variant="outline" onClick={() => onDownload(file)}>
        <Download />
        Download file
      </Button>
    </div>
  )
}
